import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { api, PAPELES, type LineaDeclarada, type TipoPallet } from '../api';
import { mensajeDeError, useApi, VIVO } from '../lib/useApi';
import { useEstadoGuardado } from '../lib/persistencia';
import { COLUMNAS_PEGAR, parsearPegado, resolverArticulo } from '../lib/pegarPlanilla';
import { useSesion } from '../lib/sesion';
import { abrir, aparecer, desplegar, revelarDesde, suave } from '../components/movimiento';
import { Cambia, Cargando, Icono, Numero, PanelError, useConfirmar, Vacio } from '../components/ui';
import {
  anchoDeCampo,
  aviso,
  botonPrimario,
  botonSecundario,
  c,
  campo,
  enlace,
  etiquetaCampo,
  hoyISO,
  miles,
  mono,
  nota,
  subtitulo,
  tarjeta,
  } from '../theme';

/**
 * Dar de alta un contenedor.
 *
 * Es la puerta de entrada de la mercadería al depósito. Reemplazó a «Cargar
 * stock», que era una planilla suelta donde se declaraba qué había sin decir
 * de dónde venía: ahora se anota el contenedor con lo que declara traer, y
 * **eso** se convierte en pallets: cada renglón dice en cuántos viene, y al
 * cargarlo se arman solos, listos para ubicar.
 *
 * Lo carga el depósito, no el cliente: la planilla la manda ZF, pero quien la
 * sube es el que después responde por lo que dice.
 */

/** El FIFO es el mes en que entra la mercadería: en septiembre, «FIFO 9». */
const fifoDe = (fecha: string) => `FIFO ${Number(fecha.slice(5, 7))}`;

/** Una clave que no cambia aunque el renglón cambie de lugar: con el índice, quitar uno animaría al último. */
let contador = 0;
const nuevaClave = () => `r${Date.now().toString(36)}${contador++}`;

/** Cómo nace un renglón: sube desde el botón de agregar. */
const nace = {
  initial: { opacity: 0, y: 28, scale: 0.97 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, scale: 0.96, transition: { duration: 0.2 } },
};

type Renglon = {
  /** Sólo para animar; un borrador viejo no la tiene y cae al índice. */
  clave?: string;
  articulo: string;
  lote: string;
  numeroParte: string;
  piezas: string;
  pallets: string;
  tipo: TipoPallet;
};

const renglonVacio = (fifo: string): Renglon => ({
  clave: nuevaClave(),
  articulo: '',
  lote: fifo,
  numeroParte: '',
  piezas: '',
  pallets: '1',
  tipo: 'fraccionamiento',
});

const VACIO = () => ({
  id: '',
  fecha: hoyISO(),
  factura: '',
  docRecepcion: '',
  lote: '',
  muelle: '',
  lineas: [renglonVacio(fifoDe(hoyISO()))],
});

/** Un renglón sin artículo ni piezas: lo que deja `VACIO`, antes de tocar nada. */
const esRenglonVacio = (l: Renglon) => l.articulo.trim() === '' && l.piezas.trim() === '';

/**
 * Que lo guardado en el borrador tenga forma de formulario: una versión
 * vieja del borrador, o cualquier otra cosa que haya quedado en la pestaña,
 * no debería romper la pantalla al entrar.
 */
function esBorradorValido(guardado: unknown): boolean {
  if (typeof guardado !== 'object' || guardado === null) return false;
  const f = guardado as Partial<ReturnType<typeof VACIO>>;
  return typeof f.id === 'string' && typeof f.fecha === 'string' && Array.isArray(f.lineas);
}

export default function NuevoContenedor({
  onVerTodos,
  onVerArmado,
  onUbicar,
  onVerContenedor,
}: {
  onVerTodos: () => void;
  onVerArmado: () => void;
  onUbicar: () => void;
  onVerContenedor: (id: string) => void;
}) {
  const datos = useApi(() => api.contenedorNuevo(), []);
  const puedeVerTodos = useSesion().vistas.includes('papeles');
  // El borrador sobrevive a un F5: se pierde con esta pestaña, no antes.
  const [form, setForm] = useEstadoGuardado('contenedor.borrador', VACIO(), esBorradorValido);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [hecho, setHecho] = useState<{ id: string; lineas: number; piezas: number; pallets: string[] } | null>(
    null,
  );
  // Sólo para avisar un código repetido: la lista completa vive en Contenedores.
  const cargados = useApi(() => api.contenedores(), [], VIVO.normal);
  const [confirmar, dialogoConfirmar] = useConfirmar();
  const [pegando, setPegando] = useState(false);
  const [textoPegado, setTextoPegado] = useState('');

  const cambiar = (cambio: Partial<ReturnType<typeof VACIO>>) => setForm((f) => ({ ...f, ...cambio }));

  const editar = (i: number, cambio: Partial<Renglon>) =>
    cambiar({ lineas: form.lineas.map((l, n) => (n === i ? { ...l, ...cambio } : l)) });

  /**
   * Elegir el material llena el número de parte.
   *
   * En este sistema el código del artículo **es** el número de parte: el
   * maestro se identifica por él (`1721423040`) y el nombre —TUBE— se repite
   * entre productos distintos. Hacerlo escribir al lado del buscador es pedir
   * dos veces el mismo dato, y el segundo sale con un dígito cambiado.
   *
   * Se pisa sólo lo que no escribió nadie: si el operario puso otro número a
   * mano, cambiar de artículo no se lo borra. El buscador acepta código o
   * nombre (`resolverArticulo`); mientras no resuelva a un código válido se
   * deja lo tipeado tal cual, para que la pantalla lo marque en vez de
   * borrarle la letra a medio escribir.
   */
  function escribirArticulo(i: number, texto: string) {
    const actual = form.lineas[i]!;
    const codigoAnterior = resolverArticulo(actual.articulo, articulos);
    const propio = actual.numeroParte.trim() === '' || actual.numeroParte.trim() === codigoAnterior;
    const codigoNuevo = resolverArticulo(texto, articulos);
    editar(i, { articulo: texto, ...(codigoNuevo && propio ? { numeroParte: codigoNuevo } : {}) });
  }

  const quitar = (i: number) => cambiar({ lineas: form.lineas.filter((_, n) => n !== i) });

  /**
   * El renglón nuevo sale con el FIFO del último: un contenedor trae casi todo
   * del mismo mes, y tipearlo en cada renglón es donde aparece el «FIFO 8» que
   * debía ser 9.
   */
  const agregar = () =>
    cambiar({ lineas: [...form.lineas, renglonVacio(form.lineas.at(-1)?.lote ?? fifoDe(form.fecha))] });

  /**
   * Duplica un renglón justo debajo del suyo.
   *
   * Un contenedor trae el mismo material en varios lotes, y volver a elegirlo
   * en el desplegable —que tiene el maestro entero— es el paso que el operario
   * repite veinte veces. La copia sale con todo puesto: lo único que hay que
   * cambiar es el lote y las piezas.
   *
   * Va debajo del original y no al final, para que los renglones del mismo
   * material queden juntos y se vean como lo que son.
   */
  function duplicar(i: number) {
    const copia = { ...form.lineas[i]!, clave: nuevaClave() };
    cambiar({ lineas: [...form.lineas.slice(0, i + 1), copia, ...form.lineas.slice(i + 1)] });
  }

  const articulos = datos.datos?.articulos ?? [];

  // Lo que se va a mandar: los renglones a medio escribir, y los que no
  // resuelven a un artículo del maestro, no cuentan.
  const lineas: LineaDeclarada[] = form.lineas
    .map((l) => ({
      articulo: resolverArticulo(l.articulo, articulos) ?? '',
      lote: l.lote.trim() || null,
      numeroParte: l.numeroParte.trim() || null,
      piezas: Math.trunc(Number(l.piezas)) || 0,
      pallets: Math.max(0, Math.trunc(Number(l.pallets)) || 0),
      tipo: l.tipo,
    }))
    .filter((l) => l.articulo && l.piezas > 0);

  const piezas = lineas.reduce((n, l) => n + l.piezas, 0);
  const pallets = lineas.reduce((n, l) => n + l.pallets, 0);
  const listo = Boolean(form.id.trim()) && form.fecha !== '' && lineas.length > 0;

  // Por qué está deshabilitado «Cargar»: sin esto, un botón gris no dice nada.
  const faltaPara = !form.id.trim()
    ? 'Falta el código del contenedor'
    : lineas.length === 0
      ? 'Falta un artículo con piezas'
      : '';

  // Renglones con artículo pero sin piezas: se arman y quedan afuera sin avisar
  // si no se marcan acá.
  const renglonesSinPiezas = form.lineas.filter(
    (l) => l.articulo.trim() !== '' && (Math.trunc(Number(l.piezas)) || 0) <= 0,
  ).length;

  // Avisar mientras se escribe, no recién al guardar: el error del servidor
  // llega después de completar todo el formulario.
  const idRepetido =
    form.id.trim() !== '' &&
    (cargados.datos ?? []).some((ct) => ct.id === form.id.trim().toUpperCase());

  // Si hay algo cargado, «Limpiar» pregunta antes de borrarlo.
  const hayAlgoCargado =
    Boolean(form.id.trim()) ||
    Boolean(form.muelle) ||
    PAPELES.some((p) => (form[p.campo] ?? '').trim() !== '') ||
    !(form.lineas.length === 1 && esRenglonVacio(form.lineas[0]!));

  // Lo pegado de Excel, listo para mostrarse antes de sumarlo al formulario.
  const filasPegadas = pegando ? parsearPegado(textoPegado, articulos) : [];
  const erroresPegado = filasPegadas
    .map((r, i) => ({ i, r }))
    .filter(({ r }) => !r.articuloValido || !r.piezasValidas);

  /**
   * Suma las filas pegadas a los renglones del formulario. Si el único
   * renglón de arriba está vacío (el que deja `VACIO`), lo reemplaza: no
   * tiene sentido dejarlo colgado arriba de lo recién pegado.
   */
  function confirmarPegado() {
    if (filasPegadas.length === 0) return;
    const ultimoLote = form.lineas.at(-1)?.lote || fifoDe(form.fecha);
    const nuevas: Renglon[] = filasPegadas.map((r) => ({
      clave: nuevaClave(),
      articulo: r.articulo,
      lote: r.lote || ultimoLote,
      numeroParte: r.numeroParte,
      piezas: r.piezas,
      pallets: r.pallets,
      tipo: r.tipo,
    }));
    const reemplaza = form.lineas.length === 1 && esRenglonVacio(form.lineas[0]!);
    cambiar({ lineas: reemplaza ? nuevas : [...form.lineas, ...nuevas] });
    setPegando(false);
    setTextoPegado('');
  }

  async function guardar() {
    setGuardando(true);
    setError('');
    try {
      const res = await api.crearContenedor({
        id: form.id,
        fecha: form.fecha,
        muelle: form.muelle || null,
        ...Object.fromEntries(PAPELES.map((p) => [p.campo, form[p.campo]])),
        lineas,
      });
      setHecho(res);
      setForm(VACIO());
      cargados.recargar();
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setGuardando(false);
    }
  }

  if (datos.cargando && !datos.datos) return <Cargando texto="Buscando artículos y muelles…" />;
  if (datos.error) return <PanelError mensaje={datos.error} onReintentar={datos.recargar} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1000 }}>
      <div>
        <div style={{ ...subtitulo, marginTop: 0 }}>
          Por acá entra la mercadería. Se anota el contenedor con lo que trae y en cuántos pallets, y los pallets se
          arman solos, listos para ubicar.
        </div>
      </div>

      <AnimatePresence>{error && <PanelError key={error} mensaje={error} />}</AnimatePresence>

      <AnimatePresence>
      {hecho && (
        <motion.div key={hecho.id} role="status" {...abrir(16)} style={{ ...aviso('ok'), display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <Icono nombre="task_alt" size={24} color={c.verde} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>
              Contenedor{' '}
              <button type="button" onClick={() => onVerContenedor(hecho.id)} title="Ver la ficha del contenedor" style={{ ...enlace, fontSize: 14, fontWeight: 600 }}>
                {hecho.id}
              </button>{' '}
              cargado
            </div>
            <div style={{ marginTop: 2, fontSize: 13, color: c.texto2 }}>
              {hecho.pallets.length > 0
                ? `${hecho.pallets.length} ${hecho.pallets.length === 1 ? 'pallet armado' : 'pallets armados'}` +
                  (hecho.pallets.length > 1
                    ? `, de ${hecho.pallets[0]} a ${hecho.pallets.at(-1)}`
                    : ` (${hecho.pallets[0]})`) +
                  ` · ${miles(hecho.piezas)} piezas. Falta ubicarlos.`
                : `${hecho.lineas} ${hecho.lineas === 1 ? 'renglón' : 'renglones'} · ${miles(hecho.piezas)} piezas esperando pallet.`}
            </div>
          </div>
          <button
            type="button"
            onClick={hecho.pallets.length > 0 ? onUbicar : onVerArmado}
            style={{ ...botonSecundario, padding: '9px 16px' }}
          >
            {hecho.pallets.length > 0 ? 'Ir a ubicar' : 'Ir a armar'}
          </button>
        </motion.div>
      )}
      </AnimatePresence>

      {articulos.length === 0 ? (
        <div style={tarjeta}>
          <Vacio
            icono="inventory_2"
            titulo="Todavía no hay artículos en el maestro"
            detalle="Un contenedor declara qué material trae, y ese material tiene que existir antes. Cargalo en Artículos y volvé."
          />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* ------------------------------------------------- el contenedor */}
          <motion.div {...aparecer} style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>El contenedor</span>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
              <label style={etiquetaCampo}>
                Código
                <input
                  value={form.id}
                  onChange={(e) => cambiar({ id: e.target.value.toUpperCase() })}
                  placeholder="TCLU-3390174"
                  autoComplete="off"
                  spellCheck={false}
                  style={{ ...campo, fontFamily: mono, borderColor: idRepetido ? c.ambar : undefined }}
                />
                <AnimatePresence initial={false}>
                  {idRepetido && (
                    <motion.span key="repetido" {...desplegar} style={{ fontSize: 12, color: c.ambar, fontWeight: 500, overflow: 'hidden' }}>
                      Ya hay un contenedor cargado con este código
                    </motion.span>
                  )}
                </AnimatePresence>
              </label>
              <label style={etiquetaCampo}>
                Fecha de arribo
                <input
                  type="date"
                  value={form.fecha}
                  onChange={(e) => cambiar({ fecha: e.target.value || hoyISO() })}
                  style={campo}
                />
              </label>
              {PAPELES.map((p) => (
                <label key={p.campo} style={etiquetaCampo}>
                  {p.nombre}
                  <input
                    value={form[p.campo]}
                    onChange={(e) => cambiar({ [p.campo]: e.target.value })}
                    placeholder={p.muestra}
                    autoComplete="off"
                    style={{ ...campo, fontFamily: mono }}
                  />
                </label>
              ))}
              <label style={etiquetaCampo}>
                Muelle
                <select
                  value={form.muelle}
                  onChange={(e) => cambiar({ muelle: e.target.value })}
                  style={{ ...campo, padding: '0 12px' }}
                >
                  <option value="">Sin asignar</option>
                  {(datos.datos?.muelles ?? []).map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div style={nota}>
              Entra como «{datos.datos?.estadoInicial}». Los papeles (factura, documento de recepción, lote) van a cada pallet, y
              después la etiqueta del capacho y la orden de trabajo ya los traen cargados. Cuando se ubica el último
              pallet queda «Descargado completo».
            </div>
          </motion.div>

          {/* ------------------------------------------------- qué trae */}
          <motion.div
            {...aparecer}
            transition={{ ...aparecer.transition, delay: 0.05 }}
            style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 14, position: 'relative' }}
          >
            <span style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>
              Qué trae{' '}
              {lineas.length > 0 && (
                <>
                  · <Numero valor={piezas} /> piezas
                </>
              )}
              {pallets > 0 && (
                <>
                  {' '}
                  en <Numero valor={pallets} desdeCero={false} /> {pallets === 1 ? 'pallet' : 'pallets'}
                </>
              )}
            </span>
            <div style={nota}>
              Las piezas se reparten parejo entre los pallets del renglón. Un renglón con cero pallets queda esperando
              en Armado, para armar un pallet mezclado. El renglón nuevo copia el FIFO del último.
            </div>

            <AnimatePresence>
              {renglonesSinPiezas > 0 && (
                <motion.div {...desplegar} key="sin-piezas">
                  <div style={aviso('warn')}>
                    Hay {renglonesSinPiezas} {renglonesSinPiezas === 1 ? 'renglón sin piezas' : 'renglones sin piezas'}: no
                    se {renglonesSinPiezas === 1 ? 'va' : 'van'} a cargar.
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Un solo datalist para todos los renglones: el navegador lo
                busca por id, no hace falta repetirlo. */}
            <datalist id="articulos-maestro">
              {articulos.map((a) => (
                <option key={a.codigo} value={a.etiqueta} />
              ))}
            </datalist>

            <AnimatePresence initial={false} mode="popLayout">
            {form.lineas.map((l, i) => {
              const articuloInvalido = l.articulo.trim() !== '' && resolverArticulo(l.articulo, articulos) === null;
              const sinPiezas = l.articulo.trim() !== '' && (Math.trunc(Number(l.piezas)) || 0) <= 0;
              const marcar = sinPiezas || articuloInvalido;
              return (
              <motion.div
                key={l.clave ?? i}
                {...nace}
                // Con el color de reposo desde el principio: si no, arranca del borde negro del navegador.
                initial={{
                  ...nace.initial,
                  backgroundColor: marcar ? c.ambarSuave : c.panel,
                  borderColor: marcar ? c.ambar : c.borde,
                }}
                animate={{
                  ...nace.animate,
                  backgroundColor: marcar ? c.ambarSuave : c.panel,
                  borderColor: marcar ? c.ambar : c.borde,
                }}
                layout="position"
                transition={suave}
                // Celular: grilla de dos columnas con el artículo a lo ancho; desde tablet, la fila que envuelve de siempre.
                className="grid grid-cols-2 items-start gap-3 tablet:flex tablet:flex-wrap tablet:items-start"
                style={{
                  // El recuadro va siempre, también con un solo renglón.
                  // Puesto sólo a partir del segundo, agregar uno le cambia el
                  // aspecto al primero y la fila salta de lugar; y el borde
                  // hace falta igual con uno solo, porque en un celular la fila
                  // envuelve en tres líneas y sin él no se ve dónde termina.
                  padding: 12,
                  border: '1px solid',
                  borderRadius: 16,
                }}
              >
                <label className="col-span-2 tablet:col-auto" style={{ ...etiquetaCampo, ...anchoDeCampo(210) }}>
                  Artículo
                  <input
                    list="articulos-maestro"
                    value={l.articulo}
                    onChange={(e) => escribirArticulo(i, e.target.value)}
                    placeholder="Código o nombre"
                    autoComplete="off"
                    style={campo}
                  />
                  <AnimatePresence initial={false}>
                    {articuloInvalido && (
                      <motion.span key="no-esta" {...desplegar} style={{ fontSize: 12, color: c.ambar, fontWeight: 500, overflow: 'hidden' }}>
                        No está en el maestro
                      </motion.span>
                    )}
                  </AnimatePresence>
                </label>
                <label style={{ ...etiquetaCampo, ...anchoDeCampo(110) }}>
                  FIFO
                  <input value={l.lote} onChange={(e) => editar(i, { lote: e.target.value })} style={campo} />
                </label>
                <label style={{ ...etiquetaCampo, ...anchoDeCampo(140) }}>
                  Nº de parte
                  <input
                    value={l.numeroParte}
                    onChange={(e) => editar(i, { numeroParte: e.target.value })}
                    style={{ ...campo, fontFamily: mono }}
                  />
                </label>
                <label style={{ ...etiquetaCampo, ...anchoDeCampo(100) }}>
                  Piezas
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={l.piezas}
                    onChange={(e) => editar(i, { piezas: e.target.value })}
                    style={campo}
                  />
                </label>
                <label style={{ ...etiquetaCampo, ...anchoDeCampo(90) }}>
                  Pallets
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={l.pallets}
                    onChange={(e) => editar(i, { pallets: e.target.value })}
                    style={campo}
                  />
                  <span style={{ fontSize: 12, color: c.texto3, fontWeight: 400 }}>{porPallet(l)}</span>
                </label>
                <label style={{ ...etiquetaCampo, ...anchoDeCampo(150) }}>
                  Sale
                  <select
                    value={l.tipo}
                    onChange={(e) => editar(i, { tipo: e.target.value as TipoPallet })}
                    style={{ ...campo, padding: '0 12px' }}
                  >
                    <option value="fraccionamiento">En capachos</option>
                    <option value="completo">Pallet completo</option>
                  </select>
                </label>
                {/* Los dos botones van en un mismo item: sueltos, al envolver la
                    fila uno puede quedar solo en su renglón. Separados entre sí
                    para que no se toque uno de taco al apuntarle al otro.
                    La fila alinea por arriba —la ayuda de Pallets crece hacia
                    abajo sin empujar su campo—, así que un título invisible,
                    igual al de los campos, deja los botones a la altura de los
                    inputs. */}
                <div className="justify-self-end self-end tablet:self-auto" style={{ ...etiquetaCampo, flex: '0 0 auto' }}>
                <span aria-hidden className="hidden tablet:block" style={{ visibility: 'hidden' }}>.</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => duplicar(i)}
                  title="Duplicar este renglón"
                  aria-label={`Duplicar el renglón ${i + 1}`}
                  style={{
                    height: 44,
                    width: 44,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Icono nombre="content_copy" size={17} color={c.texto3} />
                </button>
                <button
                  type="button"
                  onClick={() => quitar(i)}
                  disabled={form.lineas.length === 1}
                  title="Quitar este renglón"
                  aria-label={`Quitar el renglón ${i + 1}`}
                  style={{
                    height: 44,
                    width: 44,
                    border: 'none',
                    background: 'none',
                    cursor: form.lineas.length === 1 ? 'default' : 'pointer',
                    opacity: form.lineas.length === 1 ? 0.3 : 1,
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Icono nombre="close" size={18} color={c.texto3} />
                </button>
                </div>
                </div>
              </motion.div>
              );
            })}
            </AnimatePresence>

            <motion.div layout="position" transition={suave} className="grid grid-cols-1 gap-2.5 tablet:flex tablet:flex-wrap">
              <button
                type="button"
                onClick={agregar} className="tablet:self-start"
                style={{ ...botonSecundario, padding: '9px 14px' }}
              >
                <Icono nombre="add" size={18} color={c.texto3} />
                Agregar otro renglón
              </button>
              <button
                type="button"
                onClick={() => setPegando(true)} className="tablet:self-start"
                style={{ ...botonSecundario, padding: '9px 14px' }}
              >
                <Icono nombre="content_paste" size={18} color={c.texto3} />
                Pegar desde Excel
              </button>
            </motion.div>

            <AnimatePresence>
            {pegando && (
              // Nace en el botón «Pegar desde Excel» (el segundo de la fila) y se abre en círculo.
              <motion.div key="pegar" layout="position" {...revelarDesde('300px', '0px')}>
              <div style={{ ...tarjeta, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={nota}>
                  Copiá de la planilla las columnas en este orden y pegalas (Ctrl+V) acá abajo, una fila por renglón:
                  <br />
                  <span style={{ fontFamily: mono }}>{COLUMNAS_PEGAR.join('  ·  ')}</span>
                  <br />
                  Por ejemplo: <span style={{ fontFamily: mono }}>1721423040 500 2 FIFO 9 completo</span>. Si la
                  primera fila es el encabezado, se descarta sola.
                </div>
                <textarea
                  rows={6}
                  value={textoPegado}
                  onChange={(e) => setTextoPegado(e.target.value)}
                  placeholder="Pegá acá lo copiado de Excel…"
                  style={{
                    padding: '12px 14px',
                    border: `1px solid ${c.bordeFuerte}`,
                    borderRadius: 16,
                    background: c.panel,
                    fontSize: 13,
                    fontFamily: mono,
                    color: c.texto,
                    resize: 'vertical',
                  }}
                />
                <AnimatePresence initial={false}>
                {textoPegado.trim() !== '' && (
                  <motion.div key="suma" {...desplegar}>
                  <div style={nota}>
                    Se {filasPegadas.length === 1 ? 'va a sumar' : 'van a sumar'} <Numero valor={filasPegadas.length} desdeCero={false} />{' '}
                    {filasPegadas.length === 1 ? 'renglón' : 'renglones'}.
                  </div>
                  </motion.div>
                )}
                {erroresPegado.length > 0 && (
                  <motion.div key="errores" {...desplegar}>
                  <div style={aviso('warn')}>
                    {erroresPegado.length} {erroresPegado.length === 1 ? 'con error' : 'con errores'}:
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      {erroresPegado.map(({ i, r }) => (
                        <li key={i}>
                          Fila {i + 1}:{' '}
                          {!r.articuloValido && `el artículo «${r.articulo}» no existe en el maestro`}
                          {!r.articuloValido && !r.piezasValidas && ' y '}
                          {!r.piezasValidas && 'las piezas no son un número'}
                        </li>
                      ))}
                    </ul>
                  </div>
                  </motion.div>
                )}
                </AnimatePresence>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    onClick={confirmarPegado}
                    disabled={filasPegadas.length === 0}
                    style={{ ...botonPrimario, opacity: filasPegadas.length === 0 ? 0.6 : 1 }}
                  >
                    Agregar {filasPegadas.length || ''} {filasPegadas.length === 1 ? 'renglón' : 'renglones'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPegando(false);
                      setTextoPegado('');
                    }}
                    style={botonSecundario}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
              </motion.div>
            )}
            </AnimatePresence>

            <motion.div layout="position" transition={suave} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={guardar}
                disabled={guardando || !listo}
                style={{ ...botonPrimario, opacity: guardando || !listo ? 0.6 : 1 }}
              >
                <Cambia>
                  {guardando
                    ? 'Cargando…'
                    : pallets > 0
                      ? `Cargar y armar ${pallets} ${pallets === 1 ? 'pallet' : 'pallets'}`
                      : 'Cargar el contenedor'}
                </Cambia>
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (
                    hayAlgoCargado &&
                    !(await confirmar({
                      titulo: '¿Limpiar el formulario?',
                      detalle: 'Se pierde lo cargado. No se puede deshacer.',
                      boton: 'Limpiar el formulario',
                    }))
                  )
                    return;
                  setForm(VACIO());
                  setHecho(null);
                  setError('');
                }}
                style={botonSecundario}
              >
                Limpiar
              </button>
              <AnimatePresence mode="wait" initial={false}>
                {!guardando && !listo && faltaPara && (
                  <motion.span key={faltaPara} {...aparecer} style={{ fontSize: 13, color: c.texto3 }}>
                    {faltaPara}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </div>
      )}

      {puedeVerTodos && (
        <button type="button" onClick={onVerTodos} style={{ ...enlace, fontSize: 13.5, alignSelf: 'flex-start' }}>
          Ver todos los contenedores
        </button>
      )}

      {dialogoConfirmar}
    </div>
  );
}


/** «334 c/u», o «333 a 334 c/u» cuando no se reparte exacto. */
function porPallet(l: Renglon): string {
  const piezas = Math.trunc(Number(l.piezas)) || 0;
  const pallets = Math.trunc(Number(l.pallets)) || 0;
  if (pallets <= 0) return 'va a Armado';
  if (piezas <= 0) return '';
  const base = Math.floor(piezas / pallets);
  return piezas % pallets === 0 ? `${miles(base)} c/u` : `${miles(base)} a ${miles(base + 1)} c/u`;
}
